import { EXTRACT_OCR_STATUS } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import {
  ExtractOCRStatus,
  ExtractOCRStatusResult,
  FileState,
  Patient,
} from "core/types";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { useExtractOCR } from "./hooks/extract/ocr";
import { useExtractOCRStatus } from "./hooks/extract/status";

export type ExtractDocsContextValue = {
  extractOcr: ReturnType<typeof useExtractOCR>;
  extractOcrStatus: ReturnType<typeof useExtractOCRStatus>;
  utils: {
    getExtractedDocument: (
      fileId: FileState["id"],
    ) => ExtractOCRStatusResult | undefined;
    isFileExtracted: (fileId: FileState["id"]) => boolean;
    isFileExtractionFailed: (fileId: FileState["id"]) => boolean;
    isFileExtractionProcessing: (fileId: FileState["id"]) => boolean;
    isFileInExtractPipeline: (fileId: FileState["id"]) => boolean;
  };
};

const emptyFunction = (functionName: string) => () => {
  throw new Error(`${functionName} must be used within an ExtractDocsProvider`);
};

const emptyExtractDocsContextValue: ExtractDocsContextValue = {
  extractOcr: {
    errors: [],
    extractingAllFiles: false,
    handleDownloadAndRunOcr: emptyFunction("handleDownloadAndRunOcr"),
    handleOcrForFile: emptyFunction("handleOcrForFile"),
    processingFileIds: [],
    results: {},
  },
  extractOcrStatus: {
    error: null,
    isInitialResponseReceived: false,
    refetch: emptyFunction("refetch"),
    result: null,
  },
  utils: {
    getExtractedDocument: emptyFunction("getExtractedDocument"),
    isFileExtracted: emptyFunction("isFileExtracted"),
    isFileExtractionFailed: emptyFunction("isFileExtractionFailed"),
    isFileExtractionProcessing: emptyFunction("isFileExtractionProcessing"),
    isFileInExtractPipeline: emptyFunction("isFileInExtractPipeline"),
  },
};

const ExtractDocsContext = createContext<ExtractDocsContextValue | undefined>(
  undefined,
);

type ExtractDocsProviderProps = {
  children: ReactNode;
  patient: Patient;
};

const PROCESSING_STATUS: ExtractOCRStatus[] = [
  EXTRACT_OCR_STATUS.PROCESSING,
  EXTRACT_OCR_STATUS.PENDING,
];

export const ExtractDocsProvider = ({
  children,
  patient,
}: ExtractDocsProviderProps) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const extractOcrStatus = useExtractOCRStatus({
    careseekerId: patient.careseeker?.id,
    patientId: patient.id,
    skip: !isDocsAndExtractActive && !isDischargeLetterActive,
  });
  const extractOcr = useExtractOCR({
    careseekerId: patient.careseeker?.id,
    externalId: patient.external_id,
    ocrStatus: extractOcrStatus.result,
    onComplete: extractOcrStatus.refetch,
    patientId: patient.id,
  });

  const getExtractedDocument = (
    fileId: FileState["id"],
  ): ExtractOCRStatusResult | undefined => {
    return extractOcrStatus.result?.documents.find(
      (doc) => doc.file_id === String(fileId),
    );
  };

  const isFileExtracted = (fileId: FileState["id"]): boolean => {
    return (
      getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.EXTRACTED
    );
  };

  const isFileExtractionProcessing = (fileId: FileState["id"]): boolean => {
    const document = getExtractedDocument(fileId);
    if (!document) {
      return extractOcr.processingFileIds.includes(fileId);
    }

    return PROCESSING_STATUS.includes(document.status);
  };

  const isFileExtractionFailed = (fileId: FileState["id"]): boolean => {
    return getExtractedDocument(fileId)?.status === EXTRACT_OCR_STATUS.FAILED;
  };

  const isFileInExtractPipeline = (fileId: FileState["id"]): boolean => {
    return !getExtractedDocument(fileId);
  };

  const utils = {
    getExtractedDocument,
    isFileExtracted,
    isFileExtractionFailed,
    isFileExtractionProcessing,
    isFileInExtractPipeline,
  };

  const value: ExtractDocsContextValue = useMemo(
    () => ({
      extractOcr,
      extractOcrStatus,
      utils,
    }),
    [extractOcr, extractOcrStatus, utils],
  );

  return (
    <ExtractDocsContext.Provider value={value}>
      {children}
    </ExtractDocsContext.Provider>
  );
};

export const useExtractDocs = (): ExtractDocsContextValue => {
  const context = useContext(ExtractDocsContext);
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  if (!isDocsAndExtractActive && !isDischargeLetterActive) {
    return emptyExtractDocsContextValue;
  }

  if (context === undefined) {
    throw new Error(
      "useExtractDocs must be used within an ExtractDocsProvider",
    );
  }

  return context;
};
